import { useState, type FormEvent, type ReactNode } from "react";
import { can } from "@sts/schemas";
import type { Contact, Me, Property } from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal, useSession } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href, useNavigate } from "../app/router.js";
import { addressOf } from "./Contacts.jsx";

/**
 * Taking a lead.
 *
 * An enquiry arrives — a name, an address, and where it came from — and this is
 * the one screen that turns it into a job the rest of the platform can act on.
 *
 * Two things it does that are easy to leave out and expensive to leave out.
 *
 * It records the source. `jobs.source` is a plain text column nobody is forced
 * to fill in, and an installer who cannot say which of their leads came from
 * which channel is buying advertising blind. The field is optional here too, but
 * it is on the form rather than three screens away.
 *
 * It remembers the job id. The pipeline board has no index route to read from
 * and is assembled from ids this browser has been given, so a job created and
 * not remembered would be invisible on the board it was created for.
 */

interface Intake {
  readonly me: Me;
  readonly mayCreate: boolean;
  readonly contacts: readonly Contact[];
  readonly properties: readonly Property[];
}

/** Where enquiries come from, offered as suggestions rather than an enum. */
const SOURCES = [
  "Website",
  "Telephone",
  "Referral",
  "Repeat customer",
  "MCS directory",
  "Trade partner",
  "Local advertising",
];

export function NewLead(): ReactNode {
  const { client } = usePortal();

  const { state, reload } = useAsync<Intake>(async () => {
    // The role is resolved before the lists are fetched, so a role that cannot
    // create a job is told exactly that rather than being refused the contact
    // book and left to infer which of the two grants stopped it.
    const me = await client.me();
    const mayCreate = can({ role: me.role, resource: "job", action: "create" });
    if (!mayCreate) {
      return { me, mayCreate, contacts: [], properties: [] };
    }
    const [contacts, properties] = await Promise.all([
      client.contacts(),
      client.properties(),
    ]);
    return { me, mayCreate, contacts, properties };
  }, [client]);

  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.surveys()}>Surveys</a>
        </p>
        <h1>New lead</h1>
        <p className="lede">
          A job starts at <strong>Lead</strong> and moves from there. The
          customer and the property can be attached now or left for later.
        </p>
      </div>

      <Async state={state} what="the new lead form" reload={reload}>
        {(intake) =>
          intake.mayCreate ? (
            <Form intake={intake} />
          ) : (
            <div className="notice warn" role="alert">
              <p>
                Your role may not <strong>create</strong> <strong>job</strong>,
                so a lead cannot be taken here. This is a permission, not a
                fault: ask somebody in the office to open the job.
              </p>
            </div>
          )
        }
      </Async>
    </>
  );
}

function Form({ intake }: { readonly intake: Intake }): ReactNode {
  const { client, known } = usePortal();
  const session = useSession();
  const navigate = useNavigate();
  const { busy, error, submit } = useSubmit();

  const [reference, setReference] = useState("");
  const [contactId, setContactId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [source, setSource] = useState("");

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await submit(async () => {
      const job = await client.createJob({
        reference: reference.trim(),
        ...(contactId ? { contactId } : {}),
        ...(propertyId ? { propertyId } : {}),
        ...(source.trim() ? { source: source.trim() } : {}),
      });
      // Before navigating, and only after the API confirmed the job: the board
      // is built from these ids, so a job created and not remembered would not
      // appear on the pipeline it was just created for.
      known.remember("job", session?.organisationId ?? null, job.id);
      navigate(href.job(job.id));
    });
  }

  return (
    <section>
      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="field">
          <label htmlFor="lead-reference">Job reference</label>
          <input
            id="lead-reference"
            type="text"
            required
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <p className="hint small">
            Yours to choose, and unique within your organisation — the API
            refuses a duplicate. This is the number that will appear on the
            quotation, the design pack and the MCS certificate.
          </p>
        </div>

        <div className="field">
          <label htmlFor="lead-contact">Customer</label>
          <select
            id="lead-contact"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
          >
            <option value="">Not yet known</option>
            {intake.contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.fullName}
              </option>
            ))}
          </select>
          <p className="hint small">
            Somebody not on this list has to be added to the{" "}
            <a href={href.contacts()}>customer book</a> first.
          </p>
        </div>

        <div className="field">
          <label htmlFor="lead-property">Property</label>
          <select
            id="lead-property"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
          >
            <option value="">Not yet known</option>
            {intake.properties.map((property) => (
              <option key={property.id} value={property.id}>
                {addressOf(property)} · {property.postcode}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="lead-source">Where the enquiry came from</label>
          <input
            id="lead-source"
            type="text"
            list="lead-sources"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
          {/* Suggestions rather than a fixed list: the column is free text, and
              an installer who has just started advertising somewhere new should
              not have to wait for a release to record it. */}
          <datalist id="lead-sources">
            {SOURCES.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          <p className="hint small">
            Optional, and worth filling in: it is the only record of which
            channel produced this job.
          </p>
        </div>

        <button type="submit" disabled={busy || reference.trim() === ""}>
          {busy ? "Opening…" : "Open the job"}
        </button>
      </form>

      {error !== null ? <Failure error={error} what="this lead" /> : null}
    </section>
  );
}
